// src/pages/VideoIntelligenceAnalyze.js
//
// "Frame IQ" — the actual AI-powered brand-safety video analysis
// product (backed by the video_intelligence Django app). Not to be confused
// with the unrelated "Campaign IQ" menu (FrameIQCreate/List/Download.js),
// a separate YouTube-scraping/campaign tool that briefly shared this name.
//
// Two ways to run an analysis, switched by the "Live streaming" toggle:
//   - Standard:  POST /analyze         -> one JSON document when finished.
//   - Streaming: POST /analyze/stream  -> Server-Sent Events; each section
//                (summary, safety, segments, brands, ...) appears as soon as
//                the analysis has produced it. See utils/sseStream.js.
// Both feed the same <AnalysisResults> renderer.
//
// Token/cost usage is deliberately not rendered here (returned by the API,
// but hidden from end users per product decision).

import React, { useState, useEffect, useRef } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import {
  Video,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Tag,
  Eye,
  Mic,
  Sparkles,
  ShieldCheck,
  Zap,
  Circle,
  RotateCcw,
} from 'lucide-react'
import apiClient from '../utils/apiClient'
import { streamPost } from '../utils/sseStream'
import { auth } from '../firebase'

const BASE_URL = process.env.REACT_APP_API_URL
const STREAM_MODE_KEY = 'frameiq_stream_mode'

const SAFETY_STYLES = {
  safe: {
    badge: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle2,
    iconColor: 'text-green-600',
    label: 'Safe',
  },
  caution: {
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    label: 'Caution',
  },
  unsafe: {
    badge: 'bg-red-100 text-red-800 border-red-300',
    icon: XCircle,
    iconColor: 'text-red-600',
    label: 'Unsafe',
  },
}

const CONFIDENCE_STYLES = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
}

const AI_VERDICT_STYLES = {
  likely_authentic: { badge: 'bg-green-100 text-green-800', label: 'Likely Authentic' },
  uncertain: { badge: 'bg-gray-100 text-gray-700', label: 'Uncertain' },
  possibly_ai_generated: { badge: 'bg-amber-100 text-amber-800', label: 'Possibly AI-Generated' },
  likely_ai_generated: { badge: 'bg-red-100 text-red-800', label: 'Likely AI-Generated' },
}

const SEGMENT_FLAG_STYLES = {
  none: null,
  nudity_sexual_content: 'bg-red-100 text-red-700',
  violence_blood: 'bg-red-100 text-red-700',
  weapons_guns: 'bg-red-100 text-red-700',
  war_conflict: 'bg-red-100 text-red-700',
  alcohol_liquor: 'bg-amber-100 text-amber-700',
  offensive_language: 'bg-amber-100 text-amber-700',
  drug_use: 'bg-red-100 text-red-700',
  other: 'bg-amber-100 text-amber-700',
}

const Chip = ({ children }) => (
  <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full mr-2 mb-2">
    {children}
  </span>
)

const FADE_IN = { animation: 'fiqFadeIn 0.45s ease-out' }

const SectionCard = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded shadow p-6 mb-6" style={FADE_IN}>
    <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
      {Icon && <Icon size={20} className="text-gray-500" />}
      {title}
    </h2>
    {children}
  </div>
)

// Placeholder shown for a section that hasn't arrived yet in streaming mode.
const PendingBlock = () => (
  <div className="animate-pulse space-y-2" aria-hidden="true">
    <div className="h-3 bg-gray-200 rounded w-11/12" />
    <div className="h-3 bg-gray-200 rounded w-4/5" />
    <div className="h-3 bg-gray-200 rounded w-2/3" />
  </div>
)

const readStreamPreference = () => {
  try {
    // Live streaming is the default; only an explicit earlier "off" choice
    // (stored as '0') switches it off.
    return window.localStorage.getItem(STREAM_MODE_KEY) !== '0'
  } catch (e) {
    return true
  }
}

// Turns an axios error (standard mode) or a streamPost error (streaming
// mode) into one user-facing message. Both carry the backend's JSON body
// and HTTP status, just in different places.
const describeError = (error) => {
  const data = error.response?.data ?? error.data
  const status = error.response?.status ?? error.status

  if (data?.errors?.youtube_url?.length) return data.errors.youtube_url[0]
  if (data?.message) return data.message
  if (status === 401 || status === 403) {
    return 'Your session has expired or is invalid. Please log in again.'
  }
  if (error.message) return error.message
  return 'Something went wrong. Please try again.'
}

// "0:45-1:10" / "00:45" / "1:02:03" -> start time in seconds (or null).
export const parseStartSeconds = (ts) => {
  const m = /(\d+):(\d{2})(?::(\d{2}))?/.exec(String(ts || ''))
  if (!m) return null
  return m[3] !== undefined
    ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3])
    : Number(m[1]) * 60 + Number(m[2])
}

// A timestamp label. Plain text normally; a clickable "jump to this moment"
// button when the page supplies an onSeek handler (Showcase's video player).
const Timestamp = ({ value, onSeek, className }) => {
  const seconds = onSeek ? parseStartSeconds(value) : null
  if (seconds === null) return <span className={className}>{value}</span>
  return (
    <button
      type="button"
      onClick={() => onSeek(seconds)}
      title="Jump to this moment in the video"
      className={`${className} underline decoration-dotted underline-offset-2 hover:text-blue-700 cursor-pointer`}
    >
      {value}
    </button>
  )
}

// Renders an analysis object. In streaming mode the object is partial —
// sections fill in as events arrive — so `streaming` swaps "nothing here,
// hide the card" for "not here yet, show a placeholder".
export const AnalysisResults = ({ analysis, streaming, onSeek }) => {
  const safetyStyle = SAFETY_STYLES[analysis.overall_brand_safety] || null
  const aiAssessment = analysis.ai_content_assessment
  const aiVerdictStyle = aiAssessment
    ? AI_VERDICT_STYLES[aiAssessment.verdict] || AI_VERDICT_STYLES.uncertain
    : null
  const segments = analysis.segments || []
  const brands = analysis.brand_detection?.detected_brands || []

  return (
    <>
      <style>{'@keyframes fiqFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }'}</style>
      {/* Video summary + overall safety */}
      <SectionCard title="Overview" icon={ShieldCheck}>
        {analysis.video_summary ? (
          <p className="text-gray-700 mb-4">{analysis.video_summary}</p>
        ) : (
          streaming && <PendingBlock />
        )}
        {safetyStyle && (
          <div className={`border rounded p-4 flex items-start gap-3 ${safetyStyle.badge}`}>
            <safetyStyle.icon size={22} className={`${safetyStyle.iconColor} flex-shrink-0 mt-0.5`} />
            <div>
              <p className="font-semibold">Brand Safety: {safetyStyle.label}</p>
              {analysis.safety_notes && (
                <p className="text-sm mt-1 opacity-90">{analysis.safety_notes}</p>
              )}
            </div>
          </div>
        )}
      </SectionCard>

      {/* Segments timeline */}
      {(segments.length > 0 || streaming) && (
        <SectionCard title="Timeline" icon={Clock}>
          <div className="space-y-4">
            {segments.map((segment, idx) => {
              const flagStyle = SEGMENT_FLAG_STYLES[segment.safety_flag]
              return (
                <div key={idx} className="border-l-2 border-gray-200 pl-4 relative" style={FADE_IN}>
                  <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-gray-400" />
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Timestamp
                      value={segment.timestamp}
                      onSeek={onSeek}
                      className="text-sm font-mono font-semibold text-gray-600"
                    />
                    {flagStyle && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${flagStyle}`}>
                        {segment.safety_flag.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 text-sm mb-2">{segment.description}</p>
                  {segment.objects_detected?.length > 0 && (
                    <div>
                      {segment.objects_detected.map((obj, i) => (
                        <Chip key={i}>{obj}</Chip>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
            {streaming && (
              <div className="border-l-2 border-gray-200 pl-4">
                <PendingBlock />
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* Brand detection */}
      {(brands.length > 0 || streaming) && (
        <SectionCard title="Brand Detection" icon={Tag}>
          {brands.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {brands.map((brand, idx) => {
                // Count what is actually listed: the model's own
                // appearance_count sometimes disagrees with its occurrences.
                const appearances = brand.occurrences?.length ?? brand.appearance_count
                return (
                <div key={idx} className="border border-gray-200 rounded p-4" style={FADE_IN}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-800">{brand.brand_name}</span>
                    <span className="text-xs text-gray-500">
                      {appearances} {appearances === 1 ? 'appearance' : 'appearances'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {brand.occurrences?.map((occ, i) => {
                      const DetectionIcon = occ.detection_type === 'verbal_mention' ? Mic : Eye
                      return (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <DetectionIcon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <Timestamp value={occ.timestamp} onSeek={onSeek} className="font-mono text-gray-500" />
                          <span className="text-gray-600 flex-1">{occ.context}</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                              CONFIDENCE_STYLES[occ.confidence] || CONFIDENCE_STYLES.low
                            }`}
                          >
                            {occ.confidence}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
                )
              })}
            </div>
          )}
          {streaming && (
            <div className={brands.length > 0 ? 'mt-4' : ''}>
              <PendingBlock />
            </div>
          )}
        </SectionCard>
      )}

      {/* Object & context */}
      {(analysis.object_context || streaming) && (
        <SectionCard title="Object & Context Detection" icon={Sparkles}>
          {analysis.object_context ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['sports', 'locations', 'objects', 'activities'].map((key) =>
                analysis.object_context[key]?.length > 0 ? (
                  <div key={key}>
                    <p className="text-xs font-semibold uppercase text-gray-400 mb-2">{key}</p>
                    <div>
                      {analysis.object_context[key].map((item, i) => (
                        <Chip key={i}>{item}</Chip>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          ) : (
            <PendingBlock />
          )}
        </SectionCard>
      )}

      {/* AI content assessment */}
      {(aiAssessment || streaming) && (
        <SectionCard title="AI-Generated Content Assessment" icon={Sparkles}>
          {aiAssessment ? (
            <>
              <span className={`inline-block text-sm font-medium px-3 py-1 rounded-full mb-3 ${aiVerdictStyle.badge}`}>
                {aiVerdictStyle.label}
              </span>
              <p className="text-gray-600 text-sm">{aiAssessment.confidence_notes}</p>
            </>
          ) : (
            <PendingBlock />
          )}
        </SectionCard>
      )}
    </>
  )
}

const STREAM_PHASE_TEXT = {
  waiting: {
    title: 'Preparing your analysis',
    hint: 'Our AI is watching the video. The first results will appear here shortly.',
  },
  streaming: {
    title: 'Analyzing your video',
    hint: 'Results appear below as soon as each section is ready.',
  },
  retrying: {
    title: 'Service is busy, retrying',
    hint: 'Our analysis service is under high demand. Trying again automatically.',
  },
  resuming: {
    title: 'Connection interrupted, resuming',
    hint: 'Picking up the analysis where it left off.',
  },
}

// What the progress card tracks. A step is "done" once its data has arrived;
// the two list steps (timeline, brands) show a running count instead.
const buildProgressSteps = (partial) => {
  const p = partial || {}
  const segments = p.segments?.length || 0
  const brands = p.brand_detection?.detected_brands?.length || 0
  return [
    { key: 'summary', label: 'Video summary', done: !!p.video_summary },
    { key: 'safety', label: 'Brand safety', done: !!p.overall_brand_safety },
    {
      key: 'timeline',
      label: 'Timeline',
      done: segments > 0,
      detail: segments > 0 ? `${segments} ${segments === 1 ? 'moment' : 'moments'}` : null,
    },
    {
      key: 'brands',
      label: 'Brand detection',
      done: brands > 0,
      detail: brands > 0 ? `${brands} ${brands === 1 ? 'brand' : 'brands'}` : null,
    },
    { key: 'context', label: 'Objects & context', done: !!p.object_context },
    { key: 'ai', label: 'AI content check', done: !!p.ai_content_assessment },
  ]
}

const StreamProgress = ({ phase, partial }) => {
  const text = STREAM_PHASE_TEXT[phase] || STREAM_PHASE_TEXT.waiting
  const steps = buildProgressSteps(partial)
  const doneCount = steps.filter((s) => s.done).length
  const activeKey = steps.find((s) => !s.done)?.key
  // Never show an empty bar (looks stuck) or a full one before it's finished.
  const percent = Math.min(95, Math.max(6, Math.round((doneCount / steps.length) * 100)))

  return (
    <div className="bg-white rounded shadow p-5 mb-6" style={FADE_IN}>
      <div className="flex items-center gap-3 mb-4">
        <Loader2 size={22} className="animate-spin text-blue-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-gray-800 font-semibold">{text.title}</p>
          <p className="text-gray-500 text-sm mt-0.5">{text.hint}</p>
        </div>
        <span className="text-sm font-medium text-gray-400 tabular-nums">
          {doneCount}/{steps.length}
        </span>
      </div>

      <div
        className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={doneCount}
      >
        <div
          className="h-full bg-blue-600 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>

      <ul className="flex flex-wrap gap-2">
        {steps.map((step) => {
          const active = step.key === activeKey
          const cls = step.done
            ? 'bg-green-50 text-green-800 border-green-200'
            : active
            ? 'bg-blue-50 text-blue-800 border-blue-200'
            : 'bg-gray-50 text-gray-400 border-gray-200'
          return (
            <li
              key={step.key}
              className={`inline-flex items-center gap-1.5 border rounded-full px-3 py-1 text-sm transition-colors duration-500 ${cls}`}
            >
              {step.done ? (
                <CheckCircle2 size={14} />
              ) : active ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Circle size={14} />
              )}
              {step.label}
              {step.detail && <span className="opacity-70">· {step.detail}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const VideoIntelligenceAnalyze = () => {
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  // Streaming-mode state
  const [streamMode, setStreamMode] = useState(readStreamPreference)
  const [partial, setPartial] = useState(null) // analysis built up from events
  const [streamPhase, setStreamPhase] = useState('waiting')
  const [streamError, setStreamError] = useState(null) // { message, detail }
  const abortRef = useRef(null)
  const mountedRef = useRef(true)

  // Leaving the page aborts an in-flight stream. The backend sees the
  // disconnect, stops the analysis, and logs the run as cancelled.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (abortRef.current) abortRef.current.abort()
    }
  }, [])

  const toggleStreamMode = () => {
    const next = !streamMode
    setStreamMode(next)
    try {
      window.localStorage.setItem(STREAM_MODE_KEY, next ? '1' : '0')
    } catch (e) {
      // storage unavailable — the toggle still works for this session
    }
  }

  const clearRunState = () => {
    setResult(null)
    setPartial(null)
    setStreamError(null)
  }

  const runStandard = async (url) => {
    setLoading(true)
    clearRunState()

    try {
      const response = await apiClient.post('/video_intelligence/api/analyze', {
        youtube_url: url,
      })

      if (response.data?.success) {
        setResult(response.data)
        toast.success('Analysis complete')
      } else {
        toast.error(response.data?.message || 'Analysis failed')
      }
    } catch (error) {
      toast.error(describeError(error))
    } finally {
      setLoading(false)
    }
  }

  const runStream = async (url) => {
    setLoading(true)
    clearRunState()
    setStreamPhase('waiting')

    const controller = new AbortController()
    abortRef.current = controller

    let finished = false
    let failed = false

    const mergePartial = (patch) => setPartial((prev) => ({ ...(prev || {}), ...patch }))

    const handleEvent = (event, data) => {
      switch (event) {
        case 'status':
          setStreamPhase(
            data?.phase === 'resuming' || data?.phase === 'retrying' ? data.phase : 'streaming'
          )
          break
        case 'video_summary':
        case 'overall_brand_safety':
        case 'safety_notes':
        case 'object_context':
        case 'ai_content_assessment':
          mergePartial({ [event]: data })
          break
        case 'segment':
          setPartial((prev) => ({
            ...(prev || {}),
            segments: [...(prev?.segments || []), data],
          }))
          break
        case 'brand':
          setPartial((prev) => ({
            ...(prev || {}),
            brand_detection: {
              detected_brands: [...(prev?.brand_detection?.detected_brands || []), data],
            },
          }))
          break
        case 'complete':
          // The authoritative copy of everything — replaces the partial build.
          finished = true
          setResult({ success: true, analysis: data.analysis, usage: data.usage })
          setPartial(null)
          toast.success('Analysis complete')
          break
        case 'error':
          failed = true
          setStreamError({
            message: data?.message || 'Failed to analyze video.',
            detail: null, // backend detail names the upstream provider; never shown
          })
          toast.error(data?.message || 'Failed to analyze video.')
          break
        default:
          break // 'start', 'keepalive' and anything unknown need no UI
      }
    }

    try {
      const user = auth.currentUser
      if (!user) {
        const err = new Error('Your session has expired or is invalid. Please log in again.')
        err.status = 401
        throw err
      }
      const token = await user.getIdToken()

      await streamPost({
        url: `${BASE_URL}/video_intelligence/api/analyze/stream`,
        token,
        body: { youtube_url: url },
        signal: controller.signal,
        onEvent: handleEvent,
      })

      if (!finished && !failed && mountedRef.current) {
        setStreamError({
          message: 'The connection closed before the analysis finished.',
          detail: null,
        })
      }
    } catch (error) {
      if (!mountedRef.current) return
      if (error.name === 'AbortError') {
        setPartial(null)
        toast('Analysis cancelled')
      } else {
        const message = describeError(error)
        setStreamError({ message, detail: null })
        toast.error(message)
      }
    } finally {
      abortRef.current = null
      if (mountedRef.current) setLoading(false)
    }
  }

  const handleAnalyze = (e) => {
    e.preventDefault()

    const url = youtubeUrl.trim()
    if (!url) {
      toast.error('Please enter a YouTube URL')
      return
    }

    if (streamMode) runStream(url)
    else runStandard(url)
  }

  const handleReset = () => {
    clearRunState()
    setYoutubeUrl('')
  }

  const analysis = result?.analysis || partial
  const isStreaming = loading && streamMode

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: { background: '#363636', color: '#fff', padding: '16px', borderRadius: '8px' },
          success: { iconTheme: { primary: '#10B981', secondary: '#fff' } },
          error: { iconTheme: { primary: '#EF4444', secondary: '#fff' } },
        }}
      />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Video className="text-gray-700" size={26} />
          Frame Intelligence
        </h1>
        <p className="text-gray-500 mt-1">
          Brand safety, brand detection, and content analysis for any public YouTube video.
        </p>
      </div>

      {/* URL input form */}
      <div className="bg-white rounded shadow p-6 mb-6">
        <form onSubmit={handleAnalyze} className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            value={youtubeUrl}
            onChange={(e) => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-400"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded font-medium hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Analyzing...
              </>
            ) : (
              'Analyze Video'
            )}
          </button>
          {(result || streamError) && !loading && (
            <button
              type="button"
              onClick={handleReset}
              className="border border-gray-300 text-gray-700 px-6 py-2 rounded font-medium hover:bg-gray-100 transition whitespace-nowrap"
            >
              Analyze Another
            </button>
          )}
        </form>

        {/* Streaming toggle */}
        <div className="flex items-center gap-3 mt-4">
          <button
            type="button"
            role="switch"
            aria-checked={streamMode}
            aria-label="Live streaming"
            onClick={toggleStreamMode}
            disabled={loading}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed ${
              streamMode ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                streamMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <div>
            <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <Zap size={14} className="text-gray-500" />
              Live streaming
            </p>
            <p className="text-xs text-gray-500">
              Show results section by section as they are generated, instead of waiting for the
              full report.
            </p>
          </div>
        </div>
      </div>

      {/* Loading state: standard mode */}
      {loading && !streamMode && (
        <div className="bg-white rounded shadow p-10 mb-6 flex flex-col items-center text-center">
          <Loader2 size={36} className="animate-spin text-gray-400 mb-4" />
          <p className="text-gray-700 font-medium">Analyzing your video...</p>
          <p className="text-gray-500 text-sm mt-1 max-w-md">
            This usually takes 30 seconds to 1 minute. Please don't close
            this tab.
          </p>
        </div>
      )}

      {/* Loading state: streaming mode */}
      {isStreaming && (
        <StreamProgress phase={streamPhase} partial={partial} />
      )}

      {/* Streaming failure — any sections that already arrived stay visible below */}
      {streamError && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-5 mb-6 flex items-start gap-3">
          <AlertTriangle size={22} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">
              {analysis ? 'Analysis interrupted. The results below are incomplete.' : 'Analysis failed.'}
            </p>
            <p className="text-sm mt-1">{streamError.message}</p>
          </div>
          <button
            type="button"
            onClick={() => runStream(youtubeUrl.trim())}
            className="border border-red-300 text-red-700 px-4 py-2 rounded font-medium hover:bg-red-100 transition flex items-center gap-2 whitespace-nowrap"
          >
            <RotateCcw size={16} />
            Retry
          </button>
        </div>
      )}

      {/* Results (standard: once finished; streaming: as they arrive) */}
      {analysis && (!loading || streamMode) && (
        <AnalysisResults analysis={analysis} streaming={isStreaming} />
      )}
    </div>
  )
}

export default VideoIntelligenceAnalyze
